/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IPopup } from 'app/shared/elements/popup-base';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { fireEvents } from 'app/shared/lib/fire-events';
import { guid } from 'app/shared/lib/guid';
import { ProviderLocationSearch, ProviderLocationSelectCustomEvent } from 'app/cd/components/providers/provider-location-search';
import { IProviderSearchConfig } from 'app/cd/models/essette-interfaces';
import { IProviderSearchFilters } from 'app/cd/services/provider-service';
import { IProviderSearchOptions } from 'app/cd/elements/providers/provider-search-filters';
import { SetFocusEvent } from 'app/shared/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class ProviderLocationSelect extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    public providerLocation: CD.ProviderLocation | null = null;

    @bindable.objectNull()
    public config: IProviderSearchConfig | null = null;

    @bindable.objectNull()
    public options: IProviderSearchOptions | null = null;

    @bindable.boolean()
    public required: boolean = false;

    @bindable.boolean()
    public readonly: boolean = false;

    @bindable.string()
    protected providerType: string = 'cd:provider.$';

    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay })
    public providerDetails: CD.AuthProviderDetailContainer | null = null;

    @bindable.array()
    protected regions: Core.AddressRegion[] = [];
    @bindable.array()
    protected genders: Core.Gender[] = [];

    protected generalSearchTerm: string | null = null;

    protected id: guid = guid();


    ////****************** Element References ***************************************////
    protected $modal!: IPopup;
    protected $search!: ProviderLocationSearch;
    protected $editProviderButton!: HTMLElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private $element: Element,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }


    ////****************** Events ***************************************************////
    protected providerSelected(event: ProviderLocationSelectCustomEvent) {
        if(event && event.detail && event.detail.providerLocation) {
            this.providerLocation = event.detail.providerLocation;
            this.generalSearchTerm = '';
            if(!!this.$search) { this.$search.clear(); }
            if(!!this.$modal) { this.$modal.close(); }
        }
    }

    protected providerLocationChanged(newValue?: CD.ProviderLocation, oldValue?: CD.ProviderLocation): void {
        if(!newValue) {
            this.generalSearchTerm = '';
            if(!!this.$search) { this.$search.clear(); }
        }
    }

    protected providerCleared(): void {
        this.providerLocation = null;
        this.generalSearchTerm = '';
        if(!!this.$search) { this.$search.clear(); }
        if(this.$element) {
            fireEvents(this.$element, 'provider-clear');
        }
    }

    protected providerModalClose(): void {
        if(!!this.$search) this.$search.clear();
        // ensure that focus returns to the edit button after provider is selected the first time
        if(!!this.providerLocation && !this.readonly) {
            this.events.publish(new SetFocusEvent(this.$editProviderButton));
        }
    }

    ////****************** Functions ************************************************////
    protected async search(searchTerm: string): Promise<void> {

        const filters: IProviderSearchFilters = {
            isContracted: determineDefaultIsContractedValue(this.options, this.config),
            generalSearchTerm: searchTerm
        };
        try {
            this.state.beginLoad();
            this.$search.searchProviders(filters);
            this.$modal.open();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    public clear(): void {
        this.providerCleared();
    }


    ////****************** Service Calls ********************************************////

}

/**
 * Determines what the default value for the IsContracted search filter based on the options and the config
 * @param options Provider Search options
 * @param config Provider search config
 */
export function determineDefaultIsContractedValue(options: IProviderSearchOptions | null, config: IProviderSearchConfig | null): boolean {
    if(!config?.contractedSearchUrl) {
        return false;
    }

    if(!options) {
        return false;
    }

    if(options.isContracted.defaultValue || options.isContracted.isLocked) {
        return true;
    }
    return false;
}
